import {Injectable, Injector} from '@angular/core';
import {from, Observable, of, Subscription} from 'rxjs';
import {User} from '@app/core/models/user.model';
import {HttpService} from '@app/core/services/http.service';
import {Tenant} from '@app/core/models/tenant.model';
import {Router} from '@angular/router';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {Department} from '@app/core/models/department.model';
import {DatabaseNotification} from '@app/core/models/databaseNotification.model';
import {SickMessage} from '@app/core/models/sickMessage.model';
import * as Sentry from '@sentry/browser';
import Pusher from 'pusher-js';
import {
	GetHubChain,
	SetDepartment,
	SetDepartments,
	SetTenant,
	SetUser,
	SetUserType
} from '@app/core/store/actions/session.actions';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';
import {catchError, filter, map, switchMap} from 'rxjs/operators';
import {getCurrentState, urlB64ToUint8Array} from '@app/core/helpers';
import {SetNotificationNumbers, SetNotifications} from '@app/core/store/actions/notifications.actions';
import {selectNotifications} from '@app/core/store/selectors/notifications.selectors';
import {environment} from '@env/environment';
import {IWebPushAuthData} from '@app/core/interfaces/web-push-auth-data.interface';
import {GetPunchDepartment} from '@app/core/store/actions/punch-clock.actions';
import {ENOTIFICATIONTYPE} from '@app/core/enums';
import {GetSickDays} from '@app/core/store/actions/sick-days.actions';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {SetReceivedDepartmentIdsCachingCalculation} from '@app/core/store/actions/schedule.actions';
import {IQOHubChain} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-hub-chain.interface';

export class LoginContext {
	public email: string;
	public password: string;
}

export interface INotificationNumbers {
	conversation_to_messages: {};
	conversations: number;
	dashboard: number;
	message_board_posts: number;
	shifts: number;
	sick_messages: number;
	availabilities: number;
	total_amount_of_notifications: number;
}

export interface IGettingStartedState {
	gettingStarted: boolean;
	finishedFirstGroup: boolean;
	justFinishedFirstGroup: boolean;
	finishedFirstMember: boolean;
	justFinishedFirstMember: boolean;
	finishedFirstShift: boolean;
	justFinishedFirstShift: boolean;
	finishedPaymentPeriod: boolean;
	justFinishedPaymentPeriod: boolean;
}

export enum UserType {
	PUNCH_USER = 'PUNCH_USER',
	USER = 'USER',
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	public access_token: string;
	public currentUser: User;
	public pusher: any;
	public selectedDepartment: Department;
	public notifications$: Observable<DatabaseNotification[]> = this.store.select(selectNotifications);
	public iOSUser: boolean;
	public swRegistration: any;
	public sickMessages: SickMessage[] = [];
	public gettingStartedState: IGettingStartedState = {
		gettingStarted: false,
		finishedFirstGroup: false,
		justFinishedFirstGroup: false,
		finishedFirstMember: false,
		justFinishedFirstMember: false,
		finishedFirstShift: false,
		justFinishedFirstShift: false,
		finishedPaymentPeriod: false,
		justFinishedPaymentPeriod: false,
	};
	public windowWidth: number;
	public tenant: Tenant;
	public pusherMessagesChannel;
	public pusherAdminChannnel;

	private subscription: Subscription;

	constructor(
		private injector: Injector,
	) {
		this.access_token = localStorage.getItem('access_token');
		this.windowWidth = window.innerWidth;
		this.iOSUser = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
		window.addEventListener('resize', (event: any) => {
			this.windowWidth = event.target.innerWidth;
		});
		if (localStorage.getItem('user')) {
			const user = JSON.parse(localStorage.getItem('user'));
			this.interService.timezone = user.tenantTZ;
			this.currentUser = new User(this.interService.timezone, this.interService).deserialize(user);
			const selectedDepartmentId = localStorage.getItem('selectedDepartmentId');
			if (selectedDepartmentId) {
				this.selectedDepartment = this.currentUser.departments.find(department =>
					department.id === parseInt(selectedDepartmentId));
			} else {
				this.selectedDepartment = this.currentUser.departments[0];
			}
		}
	}

	public set userType(userType: UserType) {
		this.store.dispatch(new SetUserType(userType));
	}

	public get userType(): UserType {
		return getCurrentState(this.store).session.userType;
	}

	public get httpService(): HttpService {
		return this.injector.get(HttpService);
	}

	public get localStorageService(): LocalStorageService {
		return this.injector.get(LocalStorageService);
	}

	public get store(): Store<IAppState> {
		return this.injector.get(Store) as Store<IAppState>;
	}

	public get interService(): InternationalizationService {
		return this.injector.get(InternationalizationService);
	}

	public get router(): Router {
		return this.injector.get(Router);
	}

	public setAccessToken(access_token): void {
		this.access_token = access_token;
		this.httpService.access_token = this.access_token;
		localStorage.setItem('access_token', this.access_token);
	}

	public login(login: LoginContext, tenant?: Tenant): Observable<any> {
		const data = {
			email: login.email,
			password: login.password,

		};
		if (tenant) {
			this.httpService.setTenant(tenant.id);
		}

		return this.httpService.authPostJson('login', data).pipe(map((res: any) => {
			if (res.access_token !== null) {
				// Successful login
				this.interService.timezone = res.database_settings.timezone;
				this.setAccessToken(res.access_token);
				this.tenant = new Tenant(this.interService.timezone, this.interService).deserialize(res.database_settings);
				const user = new User(this.interService.timezone, this.interService).deserialize(res.user_details);
				this.setCurrentUser(user);
				if (this.currentUser.isAdmin()) {
					this.setGettingsStartedState();
				}
				this.interService.setLang(this.currentUser.language.locale);
				this.saveLoginCredentials(data);
				this.store.dispatch(new SetUserType(UserType.USER));
				this.store.dispatch(new SetTenant(this.tenant));
				this.store.dispatch(new SetUser(this.currentUser));
				this.store.dispatch(new SetDepartment(this.currentUser.departments.find(d => d.id === this.currentUser.selected_department_id)));
				this.store.dispatch(new SetDepartments(this.currentUser.departments));

				if (this.tenant.hub_chain_id && this.currentUser.isAdmin()) {
					this.store.dispatch(new GetHubChain());
				}
			}

			return res;
		}));
	}

	public getUserTenantsToLogin(email: string, password: string): Observable<Tenant[]> {
		return this.httpService.postExternalUrlJson(this.httpService.url + 'listTenants', {email, password}).pipe(
			map((res: []) => res.map((model: Tenant) => new Tenant(this.interService.timezone, this.interService).deserialize(model))),
		);
	}

	public getUserTenantsList(email: string): Observable<Tenant[]> {
		return this.httpService.getExternalUrlJson(this.httpService.url + 'listTenants', {email}).pipe(
			map((res: []) => res.map((model: Tenant) => new Tenant(this.interService.timezone, this.interService).deserialize(model))),
		);
	}

	public runDetailsCheck(): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			if (!this.loggedIn()) {
				return reject();
			}
			const sub = this.httpService.getJson('users/details', {}).subscribe((res: any) => {
				sub.unsubscribe();
				if (!res.user_details) {
					return reject(res.error);
				}
				this.interService.timezone = res.database_settings.timezone;
				// Successful login
				this.tenant = new Tenant(this.interService.timezone, this.interService).deserialize(res.database_settings);
				const user = new User(undefined, this.interService).deserialize(res.user_details);
				this.setCurrentUser(user);
				if (this.currentUser.isAdmin()) {
					this.setGettingsStartedState();
				}
				this.interService.setLang(this.currentUser.language.locale);
				this.store.dispatch(new SetUserType(UserType.USER));
				this.store.dispatch(new SetTenant(this.tenant));
				this.store.dispatch(new SetUser(this.currentUser));
				this.store.dispatch(new SetDepartment(this.currentUser.departments.find(d => d.id === this.currentUser.selected_department_id)));
				this.store.dispatch(new SetDepartments(this.currentUser.departments));
				if (this.tenant.hub_chain_id && this.currentUser.isAdmin()) {
					this.store.dispatch(new GetHubChain());
				}

				return resolve(true);
			});
		});
	}

	public runDetailsCheckPunchUser(tenantId: number, departmentId: number): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			if (!this.loggedInPunchUser()) {
				return reject();
			}
			const sub = this.httpService.getJson('punchUser/tenants/' + tenantId, {}).subscribe((res: any) => {
				sub.unsubscribe();
				if (!res.timezone) {
					return reject(res.error);
				}

				// Successful login
				this.interService.timezone = res.timezone;
				this.tenant = new Tenant(this.interService.timezone, this.interService).deserialize(res);
				this.interService.setLang(this.interService.lang.locale || res.language.locale);
				this.store.dispatch(new SetUserType(UserType.PUNCH_USER));
				this.store.dispatch(new SetTenant(res));
				this.store.dispatch(new GetPunchDepartment(departmentId));

				return resolve(true);
			});
		});
	}

	public loggedInPunchUser(): boolean {
		return (this.access_token && this.localStorageService.getAuthTenantId() && getCurrentState(this.store).session.userType === UserType.PUNCH_USER);
	}

	public loggedIn(): boolean {
		return (this.access_token && this.localStorageService.getAuthTenantId() && getCurrentState(this.store).session.userType === UserType.USER);
	}

	public logout(): Observable<any> {
		return this.httpService.patchJson('auth/logout', {});
	}

	public punchLogout(): Observable<any> {
		return this.httpService.patchJson('auth/punchUser/logout', {});
	}

	public setGettingsStartedState(): void {
		this.gettingStartedState.finishedFirstGroup = this.tenant.finished_first_group;
		this.gettingStartedState.finishedFirstMember = this.tenant.finished_first_member;
		this.gettingStartedState.finishedFirstShift = this.tenant.finished_first_shift;
		this.gettingStartedState.finishedPaymentPeriod = this.tenant.finished_payment_period;
		this.gettingStartedState.gettingStarted = this.tenant.getting_started;
	}

	public setCurrentUser(user: User): void {
		this.currentUser = user;
		this.saveSelectedDepartment(user.selected_department_id);
		localStorage.setItem('user', user.serialize());
		Sentry.setContext('user',
			{user: user.serialize()},
		);
	}

	public saveSelectedDepartment(selectedDepartmentId): void {
		localStorage.setItem('selectedDepartmentId', '' + selectedDepartmentId);
		this.selectedDepartment = this.currentUser.departments.find(department =>
			department.id === parseInt(selectedDepartmentId));
	}

	public pushNotification(notification): void {
		const prevNotifications = getCurrentState(this.store).notifications.notifications;

		const notifications = [notification, ...prevNotifications];
		this.store.dispatch(new SetNotifications(notifications));

		const prevNotificationNumbers = getCurrentState(this.store).notifications.notificationNumbers;
		const notificationNumbers: INotificationNumbers = {
			conversation_to_messages: prevNotificationNumbers.conversation_to_messages,
			conversations: prevNotificationNumbers.conversations,
			dashboard: prevNotificationNumbers.dashboard,
			message_board_posts: prevNotificationNumbers.message_board_posts,
			shifts: prevNotificationNumbers.shifts,
			sick_messages: prevNotificationNumbers.sick_messages,
			availabilities: prevNotificationNumbers.availabilities,
			total_amount_of_notifications: prevNotificationNumbers.total_amount_of_notifications,
		};

		if (notification.shift !== null) {
			notificationNumbers.sick_messages++;
		}
		if (notification.type === 2) {
			notificationNumbers.message_board_posts++;
		}
		if (notification.type === 1) {
			if (notificationNumbers.conversation_to_messages[notification.conversation.id] === undefined) {
				notificationNumbers.conversation_to_messages[notification.conversation.id] = 1;
				notificationNumbers.conversations++;
			}
		}
		if ([13, 14].indexOf(parseInt(notification.type)) !== -1) {
			notificationNumbers.availabilities++;
		}

		this.store.dispatch(new SetNotificationNumbers(this.formattedNotificationsVariable(notificationNumbers)));
	}

	public formattedNotificationsVariable(notificationNumbers: INotificationNumbers): INotificationNumbers {
		notificationNumbers.total_amount_of_notifications =
			notificationNumbers.conversations +
			notificationNumbers.dashboard +
			notificationNumbers.message_board_posts +
			notificationNumbers.availabilities +
			notificationNumbers.shifts;

		return notificationNumbers;
	}

	public getSWSupport(): Observable<{ supported: boolean, enabled: boolean }> {
		if (!('serviceWorker' in navigator) || !('PushManager' in window) || this.iOSUser) {
			return of({supported: false, enabled: false});
		}

		return from(navigator.serviceWorker.register('./assets/serviceWorker/sw.js')).pipe(
			map(swRegistration => {
				this.swRegistration = swRegistration;
			}),
			switchMap(() => this.getCurrentSubscription().pipe(
				map((pushSubscription: PushSubscription) =>
					({supported: true, enabled: pushSubscription !== null})),
			)),
			catchError(() =>
				of({supported: false, enabled: false})),
		);
	}

	public getWebPushAuthData(): Observable<IWebPushAuthData> {
		const applicationServerKey = urlB64ToUint8Array(environment.pushManagerAppKey);

		return from(
			this.swRegistration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey}),
		)
			.pipe(
				map((subscription: PushSubscription) => {
					const subArray = JSON.parse(JSON.stringify(subscription));

					return {
						public_key: subArray.keys.p256dh,
						auth_token: subArray.keys.auth,
						endpoint: subArray.endpoint,
					};
				}),
			);
	}

	public subscribeWebPushNotifications(): Observable<any> {
		return this.getWebPushAuthData().pipe(
			switchMap(webPushAuthData =>
				this.httpService.postJson<{ success: boolean }>('webTokens', webPushAuthData)),
		);
	}

	public getCurrentSubscription(): Observable<PushSubscription> {
		return from(this.swRegistration.pushManager.getSubscription()) as Observable<PushSubscription>;
	}

	public unsubscribeWebPushNotifications(): Observable<boolean> {
		return this.getCurrentSubscription().pipe(
			switchMap(pushSubscription => {
				if (pushSubscription === null) {
					return [];
				}

				const subArray = JSON.parse(JSON.stringify(pushSubscription));
				const payload = {
					endpoint: subArray.endpoint,
				};

				return this.httpService.deleteJson<{ success: boolean }>('webTokens', payload)
					.pipe(
						filter(res => res.success),
						switchMap(() => from(pushSubscription.unsubscribe())),
					);
			}),
		);
	}

	public switchDepartment(departmentId, httpService: HttpService, router: Router): void {
		if (departmentId !== this.selectedDepartment.id) {
			const data = {selected_department_id: departmentId};
			this.subscription = httpService.patchJson('selectedDepartment', data).subscribe((res: any) => {
				if (res.success) {
					const user = new User(undefined, this.interService).deserialize(res.user_details);
					this.setCurrentUser(user);
					this.selectedDepartment = this.currentUser.departments.find(department => {
						if (department.id === departmentId) {
							localStorage.setItem('selectedDepartmentId', '' + department.id);
						}

						return department.id === departmentId;
					});
					location.reload();
				} else {
					router.navigate(['./login']);
				}
				this.subscription.unsubscribe();
			});
		}
	}

	public switchDepartmentNew(departmentId: number): Observable<{ user_details: User }> {
		const data = {selected_department_id: departmentId};

		return this.httpService.patchJson<{ user_details: User }>('selectedDepartment', data).pipe(map(res => {
			res.user_details = new User(undefined, this.interService).deserialize(res.user_details);

			return res;
		}));
	}

	public subscribePusher(httpService: HttpService): void {
		this.pusher = new Pusher(httpService.pusherKey, {
			cluster: 'EU',
			encrypted: true,
			authEndpoint: httpService.urlApi + 'broadcast/auth',
			auth: {
				headers: {
					Authorization: 'Bearer ' + httpService.access_token,
				},
			},
		});
		this.pusherMessagesChannel = this.pusher.subscribe('private-Portal.' + httpService.tenantId + '-User.' + this.currentUser.id);
		this.pusherMessagesChannel.bind('Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', (notification) => {
			const formattedNotification = new DatabaseNotification(this.httpService, this.interService.timezone, this.interService).deserialize(notification);
			this.pushNotification(formattedNotification);

			if (formattedNotification.type === ENOTIFICATIONTYPE.TYPE_NEW_SICK_MESSAGE) {
				this.store.dispatch(new GetSickDays());
			}
		});

		this.pusherAdminChannnel = this.pusher.subscribe('private-Portal.' + httpService.tenantId + '-Admins');
		this.pusherAdminChannnel.bind('shifts.calculation.finished', (notification) => {
			this.store.dispatch(new SetReceivedDepartmentIdsCachingCalculation(notification.departments));
		});
	}

	public unsubscribePusher(httpService: HttpService): void {
		this.pusher.unsubscribe('private-Portal.' + httpService.tenantId + '-User.' + this.currentUser.id);
	}

	public subscribeToSickMessages(): void {
		this.notifications$.subscribe(notifications => {
			if (notifications?.length === 0) {
				return;
			}
			const notification = notifications[0];
			if (notification?.id && notification?.type === 6) {
				this.sickMessages.push(notification.sick_message);
			}
		});
	}

	public saveLoginCredentials(login: { email: string, password: string }): void {
		if (window['PasswordCredential']) {
			const cred = new window['PasswordCredential']({
				id: login.email,
				password: login.password,
			});
			navigator['credentials'].store(cred);
		}
	}

	public getUserDetails(): Observable<{ user: User, tenant: Tenant, department: Department, departments: Department[] }> {
		return this.httpService.getJson<{ user: User, tenant: Tenant }>('users/details', {}).pipe(map((res: any) => {
			const tenant = new Tenant(res.database_settings.timezone, this.interService).deserialize(res.database_settings);
			const user = new User(res.database_settings.timezone, this.interService).deserialize(res.user_details);

			return {
				user,
				tenant,
				departments: user.departments,
				department: user.departments.find(d => d.id === user.selected_department_id),
			};
		}));
	}

	public resetPassword(email: string, token: string, password: string): Observable<any> {
		const data = {
			email,
			token,
			password,
		};

		return this.httpService.postJson<{ success: boolean }>('password/reset', data);

	}

	public punchLogin(department_id: number, password: string): Observable<any> {
		const data = {
			department_id,
			password,
		};

		return this.httpService.authPostJson('punchUser/login', data);
	}

	public setNewUser(
		email: string,
		password: string,
		sign_up_token: string,
		phone_number: number,
		birth_date: string,
		phone_ext: string,
		language_id: number): Observable<any> {

		const data = {
			password,
			sign_up_token,
			phone_number,
			birth_date,
			phone_ext,
			language_id,
			email,
		};

		return this.httpService.patchJson<{ success: boolean }>('users/startInformation', data);
	}

	public sendForgotPassword(email: string): Observable<any> {
		const data = {
			email,
		};

		return this.httpService.postJson<{ success: boolean }>('password/email', data);
	}

	public saveUserProfile(data: any): Observable<any> {
		return this.httpService.patchJson('users/details', data);
	}

	public hasSeenPaymentPopup(times: number): void {
		this.httpService.patchJson('tenants/meeee', {
			has_seen_payment_popup: times,
		}).toPromise().then(r => r);
	}

	// qo-hub
	public getHubRegistrationToken(department_id: number): Observable<{ token: string }> {
		return this.httpService.getJson<{ token: string }>('tenants/signupTokens', {department_id});
	}

	public getHubChain(): Observable<IQOHubChain> {
		return this.httpService.getJson<IQOHubChain>('tenants/hubChain', {
			include: 'venues.applications',
		});
	}

}
